import { getTheme, ROMAN_TOKENS, NEON_TOKENS } from './theme';

/* ============================================================
   ARENA — theme-aware design tokens (live proxy)
   Reads from the active theme so components update on switch.
   ============================================================ */
function buildArena(theme) {
  const isNeon = theme === 'neon';
  const T = isNeon ? NEON_TOKENS : ROMAN_TOKENS;

  return {
    theme,
    isNeon,

    /* surfaces */
    stone:  T.textSoft,
    stoneL: T.border,
    stoneD: T.text,
    sand:   T.surfaceAlt,
    marble: T.surface,

    /* accent */
    gold:  T.accent,
    goldL: T.accentLight,
    goldD: T.accentDark,

    /* semantic */
    blood: T.danger,
    torch: isNeon ? '#00e5ff' : '#ff8c42',

    /* gradients */
    sandBg:    T.arenaBg,
    marbleBg:  T.surfaceGrad,
    goldFrame: T.frame,
    arenaDark: T.arenaBg,

    /* arena text */
    arenaText:     isNeon ? '#00e5ff' : '#e8c874',
    arenaTextSoft: isNeon ? 'rgba(232,240,255,.72)' : 'rgba(232,220,200,.72)',

    glow: T.glow,
  };
}

/** Live proxy — always reflects the current theme */
export const ARENA = new Proxy({}, {
  get(_, prop) {
    return buildArena(getTheme())[prop];
  },
});

/** Get a frozen snapshot for a specific theme */
export const arenaOf = (theme) => buildArena(theme);

/* ============================================================
   CLASS THEMES
   Accent colors are IDENTICAL in both themes so the AI artwork
   (heroes, pets, cars, items) always matches.
   ============================================================ */
export const CLASS_THEME = {
  warrior: {
    key: 'warrior', name: 'Warrior', icon: '⚔️', gender: 'male',
    element: 'Fire / Earth', tagline: 'Raw power, unbreakable will',
    romanTitle: 'Gladiator', neonTitle: 'Vanguard', latin: 'Ignis Bellator',
    hex: '#f97316', glow: '#fb923c', dark: '#9a3412', light: '#ffedd5',
    gradient: 'from-orange-500 via-red-500 to-amber-600',
    gradientSoft: 'from-orange-50 to-amber-50',
    ring: 'ring-orange-400', border: 'border-orange-400',
    text: 'text-orange-600', bg: 'bg-orange-500', bgSoft: 'bg-orange-50',
    shadow: 'shadow-orange-500/40',
    glowCss: '0 0 40px rgba(249,115,22,.5)',
    frameCss: 'drop-shadow(0 0 22px rgba(249,115,22,.6))',
    radial: 'radial-gradient(circle at 50% 40%, rgba(249,115,22,.30), transparent 68%)',
    arenaBg: 'linear-gradient(165deg, #2a1810 0%, #1a1008 55%, #3d2418 100%)',
    arenaBgNeon: 'linear-gradient(165deg, #1a0d08 0%, #0d0704 55%, #2a1510 100%)',
    particles: ['#f97316', '#fb923c', '#fbbf24', '#ef4444'],
    banner: 'linear-gradient(135deg, #9a3412, #7c2d12)',
  },

  mage: {
    key: 'mage', name: 'Mage', icon: '🔮', gender: 'female',
    element: 'Arcane / Ice', tagline: 'Armor means nothing',
    romanTitle: 'Augur', neonTitle: 'Codebreaker', latin: 'Arcana Vates',
    hex: '#a855f7', glow: '#c084fc', dark: '#6b21a8', light: '#f3e8ff',
    gradient: 'from-purple-500 via-violet-500 to-cyan-500',
    gradientSoft: 'from-purple-50 to-cyan-50',
    ring: 'ring-purple-400', border: 'border-purple-400',
    text: 'text-purple-600', bg: 'bg-purple-500', bgSoft: 'bg-purple-50',
    shadow: 'shadow-purple-500/40',
    glowCss: '0 0 40px rgba(168,85,247,.5)',
    frameCss: 'drop-shadow(0 0 22px rgba(168,85,247,.6))',
    radial: 'radial-gradient(circle at 50% 40%, rgba(168,85,247,.30), transparent 68%)',
    arenaBg: 'linear-gradient(165deg, #1e1030 0%, #140a24 55%, #251438 100%)',
    arenaBgNeon: 'linear-gradient(165deg, #150a28 0%, #0a0518 55%, #1c0e30 100%)',
    particles: ['#a855f7', '#c084fc', '#22d3ee', '#818cf8'],
    banner: 'linear-gradient(135deg, #6b21a8, #4c1d95)',
  },

  rogue: {
    key: 'rogue', name: 'Rogue', icon: '🗡️', gender: 'male',
    element: 'Shadow / Poison', tagline: 'Strike first, strike twice',
    romanTitle: 'Sicarius', neonTitle: 'Ghost', latin: 'Umbra Venator',
    hex: '#22c55e', glow: '#4ade80', dark: '#166534', light: '#dcfce7',
    gradient: 'from-green-500 via-emerald-500 to-purple-600',
    gradientSoft: 'from-green-50 to-emerald-50',
    ring: 'ring-green-400', border: 'border-green-400',
    text: 'text-green-600', bg: 'bg-green-500', bgSoft: 'bg-green-50',
    shadow: 'shadow-green-500/40',
    glowCss: '0 0 40px rgba(34,197,94,.5)',
    frameCss: 'drop-shadow(0 0 22px rgba(34,197,94,.6))',
    radial: 'radial-gradient(circle at 50% 40%, rgba(34,197,94,.30), transparent 68%)',
    arenaBg: 'linear-gradient(165deg, #0f2318 0%, #0a1810 55%, #143020 100%)',
    arenaBgNeon: 'linear-gradient(165deg, #081a10 0%, #04100a 55%, #0d2418 100%)',
    particles: ['#22c55e', '#4ade80', '#a855f7', '#84cc16'],
    banner: 'linear-gradient(135deg, #166534, #14532d)',
  },

  ranger: {
    key: 'ranger', name: 'Ranger', icon: '🏹', gender: 'female',
    element: 'Nature / Wind', tagline: 'Every shot counts',
    romanTitle: 'Sagittaria', neonTitle: 'Tracer', latin: 'Silva Custos',
    hex: '#14b8a6', glow: '#5eead4', dark: '#115e59', light: '#ccfbf1',
    gradient: 'from-teal-500 via-emerald-400 to-green-500',
    gradientSoft: 'from-teal-50 to-emerald-50',
    ring: 'ring-teal-400', border: 'border-teal-400',
    text: 'text-teal-600', bg: 'bg-teal-500', bgSoft: 'bg-teal-50',
    shadow: 'shadow-teal-500/40',
    glowCss: '0 0 40px rgba(20,184,166,.5)',
    frameCss: 'drop-shadow(0 0 22px rgba(20,184,166,.6))',
    radial: 'radial-gradient(circle at 50% 40%, rgba(20,184,166,.30), transparent 68%)',
    arenaBg: 'linear-gradient(165deg, #0d2624 0%, #081a18 55%, #12332f 100%)',
    arenaBgNeon: 'linear-gradient(165deg, #061c1a 0%, #03100f 55%, #0a2825 100%)',
    particles: ['#14b8a6', '#5eead4', '#4ade80', '#a3e635'],
    banner: 'linear-gradient(135deg, #115e59, #134e4a)',
  },

  guardian: {
    key: 'guardian', name: 'Guardian', icon: '🛡️', gender: 'male',
    element: 'Light / Protection', tagline: 'The wall that never falls',
    romanTitle: 'Centurion', neonTitle: 'Bulwark', latin: 'Lux Aeterna',
    hex: '#f59e0b', glow: '#fcd34d', dark: '#92400e', light: '#fef3c7',
    gradient: 'from-amber-400 via-yellow-400 to-orange-400',
    gradientSoft: 'from-amber-50 to-yellow-50',
    ring: 'ring-amber-400', border: 'border-amber-400',
    text: 'text-amber-600', bg: 'bg-amber-500', bgSoft: 'bg-amber-50',
    shadow: 'shadow-amber-500/40',
    glowCss: '0 0 40px rgba(245,158,11,.5)',
    frameCss: 'drop-shadow(0 0 22px rgba(245,158,11,.6))',
    radial: 'radial-gradient(circle at 50% 40%, rgba(245,158,11,.30), transparent 68%)',
    arenaBg: 'linear-gradient(165deg, #2e2108 0%, #1f1605 55%, #3d2c0c 100%)',
    arenaBgNeon: 'linear-gradient(165deg, #201705 0%, #100b02 55%, #2c1f08 100%)',
    particles: ['#f59e0b', '#fcd34d', '#ffffff', '#fef08a'],
    banner: 'linear-gradient(135deg, #92400e, #78350f)',
  },

  bard: {
    key: 'bard', name: 'Bard', icon: '🎵', gender: 'female',
    element: 'Sound / Charm', tagline: 'Luck favours the bold',
    romanTitle: 'Musa', neonTitle: 'Amplifier', latin: 'Carmen Vitae',
    hex: '#ec4899', glow: '#f9a8d4', dark: '#9d174d', light: '#fce7f3',
    gradient: 'from-pink-500 via-fuchsia-500 to-amber-400',
    gradientSoft: 'from-pink-50 to-fuchsia-50',
    ring: 'ring-pink-400', border: 'border-pink-400',
    text: 'text-pink-600', bg: 'bg-pink-500', bgSoft: 'bg-pink-50',
    shadow: 'shadow-pink-500/40',
    glowCss: '0 0 40px rgba(236,72,153,.5)',
    frameCss: 'drop-shadow(0 0 22px rgba(236,72,153,.6))',
    radial: 'radial-gradient(circle at 50% 40%, rgba(236,72,153,.30), transparent 68%)',
    arenaBg: 'linear-gradient(165deg, #2e0f1f 0%, #200a16 55%, #3d1428 100%)',
    arenaBgNeon: 'linear-gradient(165deg, #200818 0%, #10040c 55%, #2c0f20 100%)',
    particles: ['#ec4899', '#f9a8d4', '#fbbf24', '#e879f9'],
    banner: 'linear-gradient(135deg, #9d174d, #831843)',
  },
};

/* ============================================================
   RARITY THEMES
   ============================================================ */
export const RARITY_THEME = {
  common: {
    label: 'Common', latin: 'Vulgaris', neonLabel: 'Standard', tier: 1,
    hex: '#97815f', glow: '#b8a082',
    hexNeon: '#5d7191', glowNeon: '#8fa3c4',
    text: 'text-stone-500', bg: 'bg-stone-100', border: 'border-stone-300',
    gradient: 'from-stone-400 to-stone-500',
    shadowCss: 'none',
  },
  uncommon: {
    label: 'Uncommon', latin: 'Raritas', neonLabel: 'Enhanced', tier: 2,
    hex: '#22c55e', glow: '#4ade80',
    hexNeon: '#00ffa3', glowNeon: '#66ffc2',
    text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-400',
    gradient: 'from-green-400 to-emerald-500',
    shadowCss: '0 0 14px rgba(34,197,94,.35)',
  },
  rare: {
    label: 'Rare', latin: 'Praeclarus', neonLabel: 'Advanced', tier: 3,
    hex: '#3b82f6', glow: '#60a5fa',
    hexNeon: '#00e5ff', glowNeon: '#66f0ff',
    text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-400',
    gradient: 'from-blue-400 to-indigo-500',
    shadowCss: '0 0 20px rgba(59,130,246,.45)',
  },
  epic: {
    label: 'Epic', latin: 'Magnificus', neonLabel: 'Prototype', tier: 4,
    hex: '#a855f7', glow: '#c084fc',
    hexNeon: '#a855f7', glowNeon: '#d8b4fe',
    text: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-400',
    gradient: 'from-purple-500 to-fuchsia-600',
    shadowCss: '0 0 28px rgba(168,85,247,.55)',
  },
  legendary: {
    label: 'Legendary', latin: 'Imperialis', neonLabel: 'Mythic', tier: 5,
    hex: '#c9973a', glow: '#e8c874',
    hexNeon: '#ff2e63', glowNeon: '#ff7a9c',
    text: 'text-brand-600', bg: 'bg-brand-50', border: 'border-brand-400',
    gradient: 'from-brand-300 via-brand-400 to-brand-600',
    shadowCss: '0 0 40px rgba(201,151,58,.75)',
  },
};

/* ============================================================
   SLOT THEMES
   ============================================================ */
export const SLOT_THEME = {
  helmet: { label: 'Helm',   latin: 'Galea',   neonLabel: 'Visor',   icon: '🪖', order: 1, hint: 'Defense · Willpower' },
  weapon: { label: 'Weapon', latin: 'Gladius', neonLabel: 'Weapon',  icon: '⚔️', order: 2, hint: 'Main damage stat' },
  chest:  { label: 'Chest',  latin: 'Lorica',  neonLabel: 'Core',    icon: '🛡️', order: 3, hint: 'Vitality · Defense' },
  legs:   { label: 'Legs',   latin: 'Ocreae',  neonLabel: 'Greaves', icon: '👖', order: 4, hint: 'Endurance · Speed' },
  boots:  { label: 'Boots',  latin: 'Caligae', neonLabel: 'Boosters',icon: '👢', order: 5, hint: 'Agility · Speed' },
};

export const PET_SLOT_THEME = {
  head: { label: 'Head', latin: 'Corona',   neonLabel: 'Crown',   icon: '👑', order: 1, hint: 'Loyalty · Instinct' },
  body: { label: 'Body', latin: 'Cingulum', neonLabel: 'Harness', icon: '🎽', order: 2, hint: 'Vitality · Power' },
  feet: { label: 'Feet', latin: 'Ungulae',  neonLabel: 'Claws',   icon: '🐾', order: 3, hint: 'Agility · Power' },
};

export const CAR_SLOT_THEME = {
  engine: { label: 'Engine', latin: 'Cor',    neonLabel: 'Core',    icon: '⚙️', order: 1, hint: 'Speed · Acceleration' },
  wheels: { label: 'Wheels', latin: 'Rotae',  neonLabel: 'Drives',  icon: '🛞', order: 2, hint: 'Handling · Grip' },
  body:   { label: 'Body',   latin: 'Corpus', neonLabel: 'Chassis', icon: '🛡️', order: 3, hint: 'Durability' },
  nitro:  { label: 'Nitro',  latin: 'Ignis',  neonLabel: 'Boost',   icon: '💨', order: 4, hint: 'Boost capacity' },
};

/* ============================================================
   HELPERS
   ============================================================ */
export const themeOf = (k) => CLASS_THEME[k] || CLASS_THEME.warrior;
export const rarityOf = (k) => RARITY_THEME[k] || RARITY_THEME.common;

export const CLASS_LIST = Object.values(CLASS_THEME);
export const RARITY_LIST = Object.values(RARITY_THEME);

/** Class background adapted to the active theme */
export function classArenaBg(classKey, theme = getTheme()) {
  const c = themeOf(classKey);
  return theme === 'neon' ? (c.arenaBgNeon || c.arenaBg) : c.arenaBg;
}

/** Rarity color adapted to the active theme */
export function rarityColor(rarity, theme = getTheme()) {
  const r = rarityOf(rarity);
  return theme === 'neon' ? (r.hexNeon || r.hex) : r.hex;
}

export function rarityGlow(rarity, theme = getTheme()) {
  const r = rarityOf(rarity);
  return theme === 'neon' ? (r.glowNeon || r.glow) : r.glow;
}

/** Slot label adapted to the active theme */
export function slotLabel(slot, kind = 'hero', theme = getTheme()) {
  const map = kind === 'pet' ? PET_SLOT_THEME : kind === 'car' ? CAR_SLOT_THEME : SLOT_THEME;
  const s = map[slot];
  if (!s) return slot;
  return theme === 'neon' ? (s.neonLabel || s.label) : s.label;
}

/** Class title adapted to the active theme */
export function classTitle(classKey, theme = getTheme()) {
  const c = themeOf(classKey);
  return theme === 'neon' ? (c.neonTitle || c.name) : (c.romanTitle || c.name);
}

/** Rarity label adapted to the active theme */
export function rarityLabel(rarity, theme = getTheme()) {
  const r = rarityOf(rarity);
  return theme === 'neon' ? (r.neonLabel || r.label) : r.label;
}